import { Box } from "@dehub/theme";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import React, { useCallback, useEffect, useRef, useState } from "react";

// Runtime env (injected into index.html by entrypoint.sh from the container env)
// takes priority, then build-time CRA env, then a known-valid fallback key.
// CRA inlines REACT_APP_* at build time, so deployed images cannot read a
// build-time key — the runtime window.__ENV__ value is what works on dev/prod.
declare global {
  interface Window {
    __ENV__?: { GOOGLE_MAPS_API_KEY?: string };
  }
}

const GOOGLE_MAPS_API_KEY =
  (typeof window !== "undefined" && window.__ENV__?.GOOGLE_MAPS_API_KEY) ||
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  "AIzaSyBkAxH_ttCvI_YzjGwskVU1KD2F2UDrwBs";

// Must be a stable reference, otherwise useJsApiLoader reloads on every render.
const LIBRARIES: "places"[] = ["places"];

const containerStyle = {
  width: "100%",
  height: "100%",
};

// Sukhbaatar Square, Ulaanbaatar
const DEFAULT_CENTER = {
  lat: 47.9179864,
  lng: 106.9175798,
};

export type LatLng = {
  lat: number;
  lng: number;
};

type Props = {
  center?: LatLng | null;
  zoom?: number;
  readOnly?: boolean;
  /** Draw no marker (nothing is marked yet). */
  hideMarker?: boolean;
  onChange?: (position: LatLng) => void;
};

function GoogleMapPin({ center, zoom = 14, readOnly = false, hideMarker = false, onChange }: Props) {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const [, setMap] = useState<google.maps.Map | null>(null);
  const searchSlotRef = useRef<HTMLDivElement | null>(null);
  // Keep the latest onChange without re-running the autocomplete effect.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const onLoad = useCallback((map: google.maps.Map) => setMap(map), []);
  const onUnmount = useCallback(() => setMap(null), []);

  const emit = (e: google.maps.MapMouseEvent) => {
    if (readOnly || !onChangeRef.current || !e.latLng) return;
    onChangeRef.current({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  };

  // Mount the Places API (New) autocomplete web component over the map.
  useEffect(() => {
    const slot = searchSlotRef.current;
    if (!isLoaded || readOnly || !slot) return;

    const places = google.maps.places as unknown as {
      PlaceAutocompleteElement?: new (opts?: Record<string, unknown>) => HTMLElement;
    };
    if (!places.PlaceAutocompleteElement) return;

    const element = new places.PlaceAutocompleteElement({ includedRegionCodes: ["mn"] });
    element.style.width = "100%";
    // Without this the field turns into a black box when the OS is in dark mode.
    element.style.colorScheme = "light";
    slot.appendChild(element);

    const handleSelect = async (event: Event) => {
      const prediction = (event as unknown as { placePrediction?: { toPlace: () => any } }).placePrediction;
      if (!prediction) return;
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ["location"] });
      const location = place.location;
      if (location && onChangeRef.current) {
        onChangeRef.current({ lat: location.lat(), lng: location.lng() });
      }
    };

    element.addEventListener("gmp-select", handleSelect as EventListener);

    return () => {
      element.removeEventListener("gmp-select", handleSelect as EventListener);
      element.remove();
    };
  }, [isLoaded, readOnly]);

  const position = center?.lat && center?.lng ? center : DEFAULT_CENTER;

  if (!isLoaded) return null;

  return (
    <Box style={{ position: "relative", width: "100%", height: "100%" }}>
      {!readOnly && <Box ref={searchSlotRef} style={{ position: "absolute", top: 12, left: 12, right: 12, zIndex: 5, maxWidth: 360 }} />}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={position}
        zoom={zoom}
        onClick={emit}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}>
        {hideMarker ? null : <Marker position={position} draggable={!readOnly} onDragEnd={emit} />}
      </GoogleMap>
    </Box>
  );
}

export default React.memo(GoogleMapPin);
